import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Shield } from 'lucide-react'

export default function TermsOfService() {
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-3">
                            <img src="/logo.png" alt="nCinq Invoice" className="h-10 w-auto" />
                        </Link>
                        <Link to="/" className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 font-medium transition-colors">
                            <ArrowLeft size={16} />
                            Back to Home
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Header */}
            <div className="pt-32 pb-12 bg-gray-50 border-b border-gray-100">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl text-primary-600 mb-4 font-bold">
                        <Shield size={24} />
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
                    <p className="text-gray-600">Last updated: January 12, 2026</p>
                </div>
            </div>

            {/* Content */}
            <div className="py-16">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-primary max-w-none text-gray-700 space-y-8">
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                            <p>
                                By accessing or using nCinq Invoice ("the Service"), provided by nCinq ("we," "us," or "our"), you agree to be bound by these Terms of Service. If you do not agree to all of these terms, do not use the Service.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
                            <p>
                                nCinq Invoice is a cloud-based invoicing and business management platform. We provide tools for invoice creation, client management, expense tracking, and payment processing. We reserve the right to modify or discontinue any part of the Service at any time.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
                            <p>
                                To use certain features of the Service, you must register for an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate and complete information during registration.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscriptions and Payments</h2>
                            <p>
                                Certain features of the Service require a paid subscription. Fees are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law. We reserve the right to change our subscription fees with at least 30 days' notice.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. User Content</h2>
                            <p>
                                You retain ownership of all data, information, and content you upload to the Service ("User Content"). By using the Service, you grant us a worldwide, non-exclusive license to host, store, and process your User Content solely for the purpose of providing the service to you.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Prohibited Activities</h2>
                            <p>You agree not to:</p>
                            <ul className="list-disc pl-6 space-y-2 mt-4">
                                <li>Use the Service for any illegal purpose.</li>
                                <li>Interfere with or disrupt the integrity or performance of the Service.</li>
                                <li>Attempt to gain unauthorized access to the Service or its related systems.</li>
                                <li>Send spam or other unsolicited communications through the Service.</li>
                                <li>Automate access to the Service (crawling/scraping) without prior written consent.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Privacy</h2>
                            <p>
                                Your use of the Service is also governed by our Privacy Policy, which is incorporated into these terms by reference. Please review our Privacy Policy to understand how we collect and use your information.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
                            <p>
                                TO THE MAXIMUM EXTENT PERMITTED BY LAW, NCINQ SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
                            <p>
                                We may terminate or suspend your account and access to the Service at our sole discretion, without prior notice, for conduct that we believe violates these Terms of Service or is harmful to other users of the Service, us, or third parties, or for any other reason.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
                            <p>
                                If you have any questions about these Terms, please contact us at:
                            </p>
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                <p className="font-semibold text-gray-900">nCinq Legal Department</p>
                                <p>Email: legal@ncinq.app</p>
                                <p>Address: Georgetown, Guyana</p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-50 border-t border-gray-100 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
                    <p>© 2026 nCinq Invoice. All rights reserved.</p>
                </div>
            </footer>
        </div>
    )
}
